// ---------------------------------------------------------------------------
// YouAreYou (uru) — SimpleViewPage
//
// The single-screen verification tool shown in Simple Mode.
// Route: /quick-check (protected)
//
// Sections:
//   1. Heading
//   2. Global link copy bar
//   3. How it works
//   4. Activity feed — verified connections, with hover card on each name
//   5. Privacy note
//
// API:
//   - GET /api/uru/me           → global_link
//   - GET /api/uru/connections  → verified connections for the activity feed
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import headScanVideo from "@/assets/head-scan-transparent.webm";
import livenessImg from "@/assets/livenesscheck.png";
import idImg from "@/assets/exampleCA.jpeg";
import { getUruMe, listConnections } from "@/api/endpoints";
import { resolveAvatarUrl } from "@/types/api";
import type { Connection, ConnectionPerson } from "@/types/api";
import { copyToClipboard, formatRelativeTime } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Icon helper
// ---------------------------------------------------------------------------

function Icon({
  name,
  filled = false,
  className = "",
}: {
  name: string;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// VerifiedHoverCard — mini Verified Human card shown on name hover
// ---------------------------------------------------------------------------

const FACTOR_LABELS: { key: keyof ConnectionPerson; icon: string; label: string }[] = [
  { key: "is_gov_id_verified",   icon: "badge",      label: "Government ID" },
  { key: "is_liveness_verified", icon: "face",       label: "Liveness Scan" },
  { key: "is_phone_verified",    icon: "smartphone", label: "Phone" },
  { key: "is_email_verified",    icon: "mail",       label: "Email" },
];

function VerifiedHoverCard({ person }: { person: ConnectionPerson }) {
  const avatarUrl = resolveAvatarUrl(person.avatar) ?? person.face_image_url ?? null;
  const initials = [person.first_name[0], person.last_name[0]].join("").toUpperCase();
  const fullName = `${person.first_name} ${person.last_name}`.trim();
  const activeBadges = FACTOR_LABELS.filter((f) => person[f.key] === true);

  return (
    <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-56 -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-200/60">
      {/* Blue header strip */}
      <div className="bg-primary-500 pb-8 pt-4" />

      {/* Avatar overlapping strip */}
      <div className="-mt-7 flex justify-center px-4">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={fullName}
            className="h-14 w-14 rounded-full border-2 border-white object-cover shadow"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white bg-primary-100 shadow">
            <span className="text-lg font-bold text-primary-500">{initials}</span>
          </div>
        )}
      </div>

      {/* Name + badge */}
      <div className="px-4 pb-1 pt-2 text-center">
        <p className="text-sm font-bold text-text-primary">{fullName}</p>
        <p className="mt-0.5 flex items-center justify-center gap-1 text-[11px] font-semibold text-primary-500">
          <Icon name="verified" filled className="text-xs" />
          Verified Human
        </p>
      </div>

      {/* Factor chips */}
      {activeBadges.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1 px-3 pb-3 pt-1">
          {activeBadges.map((b) => (
            <span
              key={b.key as string}
              className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700 ring-1 ring-green-200"
            >
              <Icon name={b.icon} className="text-[11px]" />
              {b.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityRow — single verified connection row
// ---------------------------------------------------------------------------

function ActivityRow({ conn }: { conn: Connection }) {
  const [hovered, setHovered] = useState(false);
  const person = conn.connected_customer;
  if (!person) return null;

  const fullName = `${person.first_name} ${person.last_name}`.trim();
  const avatarUrl = resolveAvatarUrl(person.avatar) ?? person.face_image_url ?? null;
  const initials = [person.first_name[0], person.last_name[0]].join("").toUpperCase();

  return (
    <li className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
      {/* Avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={fullName}
          className="h-9 w-9 shrink-0 rounded-full border border-gray-200 object-cover"
        />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100">
          <span className="text-xs font-bold text-primary-600">{initials}</span>
        </div>
      )}

      {/* Name + time */}
      <div className="min-w-0 flex-1">
        {/* Name with hover card */}
        <span className="relative inline-block">
          <Link
            to={`/connections/${conn.id}`}
            className="text-sm font-semibold text-primary-600 hover:underline"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {fullName}
          </Link>
          {hovered && <VerifiedHoverCard person={person} />}
        </span>
        <span className="text-sm text-text-secondary"> verified</span>
      </div>

      {/* Time + check */}
      <div className="flex shrink-0 items-center gap-1.5">
        <Icon name="check_circle" filled className="text-base text-green-500" />
        <span className="text-xs text-text-muted">
          {formatRelativeTime(conn.created)}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// MockActivityRow — shown when there are no real connections (italicised)
// ---------------------------------------------------------------------------

function MockActivityRow() {
  const [hovered, setHovered] = useState(false);
  const mockPerson: ConnectionPerson = {
    id: 0,
    uuid: "",
    first_name: "Jamie",
    last_name: "Turner",
    is_gov_id_verified: true,
    is_liveness_verified: true,
    is_phone_verified: true,
  };

  return (
    <li className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
      {/* Avatar placeholder */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 opacity-50">
        <span className="text-xs font-bold text-primary-600">JT</span>
      </div>

      {/* Name + time */}
      <div className="min-w-0 flex-1 italic text-text-muted">
        <span className="relative inline-block">
          <Link
            to="/quick-check/demo"
            className="text-sm font-semibold text-primary-400 hover:underline"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            Jamie Turner
          </Link>
          {hovered && <VerifiedHoverCard person={mockPerson} />}
        </span>
        <span className="text-sm"> verified</span>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 opacity-50">
        <Icon name="check_circle" filled className="text-base text-green-400" />
        <span className="text-xs text-text-muted">just now</span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ActivityFeed
// ---------------------------------------------------------------------------

function ActivityFeed() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listConnections({ status: "verified", sort: "-created", size: 20 })
      .then((conns) => {
        if (cancelled) return;
        // Only show connections where the other person verified — they have a connected_customer
        setConnections(conns.filter((c) => c.connected_customer !== null));
      })
      .catch(() => {/* non-critical */})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mt-6">
      <p className="mb-4 text-[10px] font-bold uppercase tracking-widest text-text-muted">
        Activity Feed
      </p>

      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : connections.length > 0 ? (
        <ul className="space-y-2">
          {connections.map((conn) => (
            <ActivityRow key={conn.id} conn={conn} />
          ))}
        </ul>
      ) : (
        <>
          <ul className="space-y-2">
            <MockActivityRow />
          </ul>
          <p className="mt-2 text-center text-xs italic text-text-muted">
            Example — hover the name to preview their profile.{" "}
            <Link to="/quick-check/demo" className="text-primary-400 hover:underline">
              See full demo →
            </Link>
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleViewPage
// ---------------------------------------------------------------------------

export default function SimpleViewPage() {
  const [globalLink, setGlobalLink] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [globalCopied, setGlobalCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getUruMe()
      .then((me) => {
        if (cancelled) return;
        if (me.global_link) setGlobalLink(me.global_link);
      })
      .catch(() => {/* non-critical */})
      .finally(() => { if (!cancelled) setProfileLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function handleCopyGlobal() {
    if (!globalLink) return;
    await copyToClipboard(globalLink);
    setGlobalCopied(true);
    setTimeout(() => setGlobalCopied(false), 2000);
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-8 md:py-12">
      {/* Heading */}
      <div className="mb-8 text-center">
        {/* Head video */}
        <div className="flex justify-center">
          <video
            src={headScanVideo}
            autoPlay
            muted
            playsInline
            style={{ height: 205, width: "auto" }}
          />
        </div>

        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-text-primary md:text-3xl">
          Check if they&apos;re really real
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Share a link — they verify their ID in minutes, you&apos;ll know they&apos;re real.
        </p>

      </div>

      {/* Step 1 — Copy and send link */}
      {profileLoading ? (
        <div className="h-24 animate-pulse rounded-2xl bg-gray-100" />
      ) : globalLink ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="mb-3 text-sm font-extrabold text-text-primary">
            1. Copy and send your YouAreYou link
          </p>
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <Icon name="link" className="shrink-0 text-base text-text-muted" />
              <span className="truncate text-sm text-text-secondary">{globalLink}</span>
            </div>
            <button
              onClick={() => void handleCopyGlobal()}
              className="flex shrink-0 items-center gap-1.5 rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              <Icon
                name={globalCopied ? "check" : "content_copy"}
                filled={globalCopied}
                className="text-base"
              />
              <span className="hidden sm:inline">{globalCopied ? "Copied!" : "Copy"}</span>
            </button>
          </div>
        </div>
      ) : null}

      {/* Steps 2 + 3 side by side */}
      <div className="mt-4 flex gap-4">
        {/* Step 2 — ID scan */}
        <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="mb-3 text-sm font-extrabold text-text-primary">
            2. They scan their ID
          </p>
          <img
            src={idImg}
            alt="ID scan"
            className="w-full rounded-lg border border-gray-200"
            style={{ height: 150, objectFit: "contain" }}
          />
        </div>

        {/* Step 3 — Liveness */}
        <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="mb-3 text-sm font-extrabold text-text-primary">
            3. They prove they&apos;re a real person
          </p>
          <img
            src={livenessImg}
            alt="Liveness check"
            className="w-full rounded-lg border border-gray-200"
            style={{ height: 150, objectFit: "contain" }}
          />
        </div>
      </div>

      {/* Step 4 — SMS notification */}
      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-extrabold text-text-primary">
          4. You get a text when they&apos;ve completed
        </p>
      </div>

      {/* Activity feed */}
      <ActivityFeed />

      {/* Privacy note */}
      <div className="mt-6 flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <Icon name="shield" filled className="mt-0.5 shrink-0 text-base text-primary-400" />
        <p className="text-xs text-text-muted">
          Verification is handled by MojoVerify. Neither party ever sees the other&apos;s documents.{" "}
          <Link to="/privacy" className="font-medium text-primary-500 hover:underline">
            Privacy policy →
          </Link>
        </p>
      </div>
    </div>
  );
}
